import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
const STATUS_COLOR = {
  // Answered, with nothing to return. Not a fault, and colouring it like one sends people
  // hunting for a bug that is really an empty cycle.
  'nothing to return': 'default',
  ok: 'success',
  failed: 'error',
  'needs a record': 'warning',
  'not enabled': 'default'
};

/**
 * Read from the MCP service rather than a copy kept here. A hand-maintained tool list goes stale
 * quietly and then misrepresents what is actually enabled.
 */
export function ToolsTab() {
  var _check$results;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    matrix = _useState2[0],
    setMatrix = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    check = _useState4[0],
    setCheck = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    running = _useState8[0],
    setRunning = _useState8[1];
  const _useState9 = useState('all'),
    _useState0 = _slicedToArray(_useState9, 2),
    filter = _useState0[0],
    setFilter = _useState0[1];
  const load = () => Promise.all([livyJson('/tools').then(setMatrix), livyJson('/tools/check').then(d => setCheck(d.check))]).catch(e => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  const runCheck = async () => {
    setRunning(true);
    setError(null);
    try {
      setCheck(await livyJson('/tools/check', {
        method: 'POST',
        body: {}
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };
  if (error) return /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mt: '16px'
    }
  }, error);
  if (!matrix) return /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    sx: {
      mt: '16px'
    }
  }, [0, 1, 2, 3].map(i => /*#__PURE__*/React.createElement(Skeleton, {
    key: i,
    height: 34
  })));
  const byName = Object.fromEntries(((_check$results = check === null || check === void 0 ? void 0 : check.results) !== null && _check$results !== void 0 ? _check$results : []).map(r => [r.name, r]));
  const shown = matrix.tools.filter(t => {
    var _byName$t$name;
    if (filter === 'enabled') return Object.values(t.roles).some(Boolean);
    if (filter === 'failed') return ((_byName$t$name = byName[t.name]) === null || _byName$t$name === void 0 ? void 0 : _byName$t$name.status) === 'failed';
    return true;
  });
  const stat = (value, label, tone) => /*#__PURE__*/React.createElement(Box, {
    sx: {
      border: 1,
      borderColor: tone ? "".concat(tone, ".main") : 'divider',
      borderRadius: '10px',
      px: '14px',
      py: '10px',
      minWidth: 96
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1.1,
      color: tone ? "".concat(tone, ".main") : 'text.primary'
    }
  }, value), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary'
    }
  }, label));
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      pt: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '14px',
      maxWidth: 760
    }
  }, "Every tool the assistant could call, read from the service itself. Enabling is per role, so most exist without being reachable. The check calls each enabled read tool and reports whether it answers and whether its answer stays inside one company."), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "10px",
    sx: {
      mb: '16px',
      flexWrap: 'wrap',
      rowGap: '10px'
    }
  }, stat(matrix.total, 'tools defined'), stat(matrix.enabledAnywhere, 'enabled somewhere'), stat(matrix.writes, 'writes, never model-called'), matrix.roles.map(r => stat(r.count, r.label))), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    alignItems: "center",
    sx: {
      mb: '14px',
      flexWrap: 'wrap',
      rowGap: '10px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    onClick: runCheck,
    disabled: running,
    sx: {
      textTransform: 'none'
    }
  }, running ? 'Checking…' : 'Run check'), check ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary'
    }
  }, "Last checked ", new Date(check.checkedAt).toLocaleString(), check.checkedBy ? " by ".concat(check.checkedBy) : ''), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    color: "success",
    variant: "outlined",
    label: "".concat(check.ok, " answered")
  }), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    color: "warning",
    variant: "outlined",
    label: "".concat(check.needsRecord, " need a record")
  }), check.noData > 0 && /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "outlined",
    label: "".concat(check.noData, " nothing to return")
  }), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    color: check.failed ? 'error' : 'default',
    variant: "outlined",
    label: "".concat(check.failed, " failed")
  }), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    color: check.risks ? 'error' : 'success',
    variant: check.risks ? 'filled' : 'outlined',
    label: check.risks ? "".concat(check.risks, " scoping risks") : 'no scoping risks'
  })) : /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary'
    }
  }, "Never checked.")), running && /*#__PURE__*/React.createElement(LinearProgress, {
    sx: {
      mb: '12px',
      height: 3,
      borderRadius: 2
    }
  }), /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    size: "small",
    exclusive: true,
    value: filter,
    onChange: (_, v) => v && setFilter(v),
    sx: {
      mb: '12px'
    }
  }, ['all', 'enabled', 'failed'].map(v => /*#__PURE__*/React.createElement(ToggleButton, {
    key: v,
    value: v,
    sx: {
      textTransform: 'none',
      px: '12px',
      py: '3px',
      fontSize: 12.5
    }
  }, v === 'all' ? "All ".concat(matrix.total) : v === 'enabled' ? 'Enabled' : 'Failing'))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      overflowX: 'auto',
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px'
    }
  }, /*#__PURE__*/React.createElement(Table, {
    size: "small",
    stickyHeader: true
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Tool"), matrix.roles.map(r => /*#__PURE__*/React.createElement(TableCell, {
    key: r.role,
    align: "center"
  }, r.label)), /*#__PURE__*/React.createElement(TableCell, null, "Health"), /*#__PURE__*/React.createElement(TableCell, null, "Scope"))), /*#__PURE__*/React.createElement(TableBody, null, shown.map(t => {
    var _t$description, _c$detail, _STATUS_COLOR$c$statu;
    const c = byName[t.name];
    return /*#__PURE__*/React.createElement(TableRow, {
      key: t.name,
      hover: true
    }, /*#__PURE__*/React.createElement(TableCell, {
      sx: {
        fontFamily: 'monospace',
        fontSize: 12
      }
    }, /*#__PURE__*/React.createElement(Tooltip, {
      title: (_t$description = t.description) !== null && _t$description !== void 0 ? _t$description : '',
      placement: "top-start"
    }, /*#__PURE__*/React.createElement("span", null, t.name)), t.write && /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      label: "write",
      color: "warning",
      variant: "outlined",
      sx: {
        ml: '8px',
        height: 18
      }
    }), t.pmsOnly && /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      label: t.pmsOnly,
      variant: "outlined",
      sx: {
        ml: '6px',
        height: 18
      }
    })), matrix.roles.map(r => /*#__PURE__*/React.createElement(TableCell, {
      key: r.role,
      align: "center",
      sx: {
        color: t.roles[r.role] ? 'success.main' : 'text.disabled'
      }
    }, t.roles[r.role] ? '●' : '–')), /*#__PURE__*/React.createElement(TableCell, null, c ? /*#__PURE__*/React.createElement(Tooltip, {
      title: (_c$detail = c.detail) !== null && _c$detail !== void 0 ? _c$detail : c.needs ? "needs ".concat(c.needs.join(', ')) : '',
      placement: "top"
    }, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      color: (_STATUS_COLOR$c$statu = STATUS_COLOR[c.status]) !== null && _STATUS_COLOR$c$statu !== void 0 ? _STATUS_COLOR$c$statu : 'default',
      label: c.status
    })) : /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 11.5,
        color: 'text.disabled'
      }
    }, "\u2013")), /*#__PURE__*/React.createElement(TableCell, null, (c === null || c === void 0 ? void 0 : c.scope) && c.scope !== 'no company data' && /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: c.risk ? 'filled' : 'outlined',
      color: c.risk ? 'error' : 'default',
      label: c.risk ? "".concat(c.scope, ", reachable by a customer") : c.scope
    })));
  })))));
}